import Layout from '@/components/feature/Layout';
import { PageBreadcrumb, RelatedLinks, ctaSidebar } from '@/pages/shared/PageHelpers';
import { applications } from '@/mocks/applications';
import { products } from '@/mocks/products';
import { Link, useParams } from 'react-router-dom';

export default function AplicacaoDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const app = applications.find((a) => a.id === slug);

  if (!app) {
    return (
      <Layout>
        <div className="w-full px-4 md:px-6 lg:px-10 pt-28 md:pt-32 pb-16 md:pb-24 text-center">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 uppercase mb-4">Aplicação não encontrada</h1>
          <p className="text-foreground-500 text-sm mb-8">A aplicação que você procura não existe ou foi removida.</p>
          <Link
            to="/aplicacoes"
            className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-bold text-sm px-8 py-4 rounded-full transition-all cursor-pointer whitespace-nowrap uppercase tracking-wide"
          >
            <i className="ri-arrow-left-line" /> Ver todas as aplicações
          </Link>
        </div>
      </Layout>
    );
  }

  const recommended = app.recommendedModels
    .map((slug) => products.find((p) => p.slug === slug))
    .filter((p): p is NonNullable<typeof p> => Boolean(p));

  const related = applications.filter((a) => a.id !== app.id).slice(0, 6);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${app.title} — Aplicação de Máquina de Solda de Geomembrana`,
    description: app.description,
    url: `https://maquinadesoldageomembrana.com.br/aplicacoes/${app.id}`,
    inLanguage: 'pt-BR',
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      <div className="w-full px-4 md:px-6 lg:px-10 pt-28 md:pt-32 pb-16 md:pb-24">
        <PageBreadcrumb items={[{ label: 'Aplicações', to: '/aplicacoes' }, { label: app.title }]} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-12">
          <div className="lg:col-span-2">
            <div className="relative w-full h-52 md:h-72 rounded-2xl overflow-hidden mb-8">
              <img
                alt={`${app.title} — aplicação de máquina de solda geomembrana`}
                title={`${app.title} — máquina de solda geomembrana`}
                className="absolute inset-0 w-full h-full object-cover object-top"
                src={app.image}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 md:bottom-6 md:left-6 flex items-center gap-3">
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-white/90">
                  <i className={`${app.icon} text-primary-600 text-xl md:text-2xl`} />
                </div>
                <div>
                  <p className="text-white/70 text-[11px] uppercase tracking-widest font-bold">Aplicação</p>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase leading-tight">{app.title}</h1>
                </div>
              </div>
            </div>

            <p className="text-foreground-400 text-sm font-medium mb-6">{app.tagline}</p>

            <div className="space-y-5 text-foreground-600 leading-relaxed">
              <p className="text-sm md:text-base">{app.longDescription}</p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">
                Como funciona a aplicação
              </h2>
              <p className="text-sm md:text-base">{app.howItWorks}</p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">
                Principais benefícios
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {app.benefits.map((benefit) => (
                  <div key={benefit} className="flex items-start gap-3 bg-background-100 rounded-xl border border-background-200/70 p-4">
                    <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 rounded-full bg-primary-50 mt-0.5">
                      <i className="ri-check-line text-primary-600 text-sm" />
                    </div>
                    <span className="text-foreground-700 text-sm leading-relaxed">{benefit}</span>
                  </div>
                ))}
              </div>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">
                Equipamentos recomendados
              </h2>
              <p className="text-sm md:text-base">
                Para essa aplicação, recomendamos os seguintes modelos do nosso catálogo:
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
              {recommended.map((p) => (
                <Link
                  key={p.id}
                  to={`/modelos-de-maquinas/${p.slug}`}
                  className="group bg-white rounded-2xl border border-background-200/70 overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-1 hover:border-primary-300 cursor-pointer"
                >
                  <div className="relative w-full h-40 bg-gradient-to-br from-background-100 to-background-200 flex items-center justify-center p-4">
                    {p.badge && (
                      <span className="absolute top-3 left-3 bg-primary-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide">
                        {p.badge}
                      </span>
                    )}
                    <img alt={p.name} className="h-full w-full object-contain" src={p.image} />
                  </div>
                  <div className="p-4 flex items-center justify-between">
                    <div className="min-w-0">
                      <h3 className="font-heading text-base font-bold text-foreground-950 uppercase">{p.name}</h3>
                      <p className="text-foreground-400 text-xs">{p.specs.potencia}</p>
                    </div>
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-50 group-hover:bg-primary-500 transition-colors flex-shrink-0">
                      <i className="ri-arrow-right-line text-primary-600 group-hover:text-white text-sm" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <RelatedLinks
              links={[
                { to: '/modelos-de-maquinas', label: 'Comparativo de Modelos' },
                { to: '/maquina-de-solda-de-geomembrana', label: 'Máquina de Solda de Geomembrana' },
                { to: '/soldagem-de-geomembrana', label: 'Técnicas de Soldagem' },
                ...related.map((r) => ({ to: `/aplicacoes/${r.id}`, label: r.title })),
              ]}
            />
          </div>

          <div className="space-y-6">{ctaSidebar}</div>
        </div>
      </div>
    </Layout>
  );
}